import { useEffect, useState } from "react";

import type { CarouselApi } from "@theme/components/primitives/Carousel/types";
import type { EmblaEventType } from "embla-carousel";

const DEFAULT_EVENTS = ["select", "reInit"] as const;
type EmblaDefined = NonNullable<CarouselApi>;

/**
 * Reads a value from Embla via `selector` and keeps it in React state.
 * Subscribes to "select" and "reInit" by default.
 */
export const useEmblaSelector = <T>(
  api: CarouselApi | undefined,
  selector: (api: EmblaDefined) => T,
  initial: T,
  events: readonly EmblaEventType[] = DEFAULT_EVENTS,
): T => {
  const [value, setValue] = useState<T>(initial);

  useEffect(() => {
    if (!api) return;

    const read = () => setValue(selector(api as EmblaDefined));
    read();

    for (const e of events) api.on(e, read);

    return () => {
      for (const e of events) api.off(e, read);
    };
  }, [api, selector, events]);

  return value;
};
